import { useEffect, useState } from "react";
import Popup from "../../../hoc/Popup/Popup";
import Spinner from "../../../components/UI/Spinner/Spinner";
import { getCoachAvailabilityHistory } from "../../../api/coaches/coaches";
import { ICoachAvailabilityHistoryObject } from "../../../types/api";
import { formatDateToSwedish, getMondayOfWeek } from "../../../shared/utility";
import { formatDate } from "../../../shared/dateUtils";
import "./CoachAvailabilityHistoryPopup.scss";

interface ICoachAvailabilityHistoryPopupProps {
  showPopup: boolean;
  onClose: () => void;
  coachId?: string;
  coachName?: string;
  /** Any date in the week being shown; the history follows the same week. */
  weekDate: Date;
}

function CoachAvailabilityHistoryPopup(
  props: ICoachAvailabilityHistoryPopupProps,
) {
  const { showPopup, onClose, coachId, coachName, weekDate } = props;

  const monday = getMondayOfWeek(weekDate);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const fromDate = formatDate(monday, "yyyy-MM-dd");
  const toDate = formatDate(sunday, "yyyy-MM-dd");
  const [history, setHistory] = useState<ICoachAvailabilityHistoryObject[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!showPopup || !coachId) return;
    let cancelled = false;
    setLoading(true);
    getCoachAvailabilityHistory(coachId, fromDate, toDate)
      .then((items) => {
        if (!cancelled) setHistory(items);
      })
      .catch(() => {
        // The wrapper has already surfaced the message; just don't leave the
        // empty state claiming there were no changes when the query failed.
        if (!cancelled) setHistory([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [showPopup, coachId, fromDate, toDate]);

  return (
    <Popup
      showPopup={showPopup}
      onClose={onClose}
      leftTopElement={
        <span className="text-m">
          Ändringar i tillgänglighet{coachName ? ` — ${coachName}` : ""} (
          {fromDate} – {toDate})
        </span>
      }
    >
      <div className="coach-history-wrapper">
        {loading && <Spinner />}

        {!loading && history.length === 0 && (
          <p className="text-m secondary-text">
            Inga ändringar registrerade för denna vecka. Byt vecka för att se
            andra ändringar. Historik började sparas när funktionen infördes —
            äldre ändringar finns inte.
          </p>
        )}

        {!loading &&
          history.map((item, index) => {
            // Same shape as SlotHistoryPopup, so the two read alike.
            const { weekday, dateAndMonth, hour } = formatDateToSwedish(
              new Date(item.created_at),
            );
            return (
              <div key={index} className="coach-history-item">
                <div className="coach-history-when text-s secondary-text">
                  {weekday}, {dateAndMonth} - kl. {hour.split(".").join(":")}
                </div>
                <div className="coach-history-event text-m">{item.event}</div>
                <div className="coach-history-by text-s secondary-text">
                  Gjordes av: {item.created_by}
                </div>
              </div>
            );
          })}
      </div>
    </Popup>
  );
}

export default CoachAvailabilityHistoryPopup;
